import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@foundation/components/ui/dialog';
import { Button } from '@foundation/components/ui/button';
import { Textarea } from '@foundation/components/ui/textarea';
import {
  Sparkles,
  Loader2,
  FileText,
} from 'lucide-react';

type SectionRegenerateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sectionTitle: string;
  onRegenerate: (prompt: string) => void;
  isRegenerating?: boolean;
};

const SectionRegenerateDialog: React.FC<SectionRegenerateDialogProps> = ({
  open,
  onOpenChange,
  sectionTitle,
  onRegenerate,
  isRegenerating = false,
}) => {
  const [additionalPrompt, setAdditionalPrompt] = useState('');

  // Reset prompt when dialog opens
  useEffect(() => {
    if (open) {
      setAdditionalPrompt('');
    }
  }, [open]);

  const handleRegenerate = () => {
    onRegenerate(additionalPrompt.trim());
  };

  const handleCancel = () => {
    if (!isRegenerating) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={isRegenerating ? undefined : onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#33b864]" />
            Regenerate Section
          </DialogTitle>
          <DialogDescription>
            Provide additional instructions to guide the AI when regenerating this section.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Section being regenerated */}
          <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
            <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-xs text-gray-500">Section to regenerate:</p>
              <p className="text-sm font-medium text-gray-900 truncate">{sectionTitle}</p>
            </div>
          </div>

          {/* Additional Instructions */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Additional Instructions
              <span className="text-gray-400 font-normal ml-1">(optional)</span>
            </label>
            <Textarea
              value={additionalPrompt}
              onChange={(e) => setAdditionalPrompt(e.target.value)}
              placeholder="Add specific instructions for regenerating this section..."
              className="min-h-[100px] resize-y"
              maxLength={1000}
              disabled={isRegenerating}
            />
            <p className="text-xs text-gray-400 mt-1">
              {additionalPrompt.length}/1000 characters
            </p>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isRegenerating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRegenerate}
            disabled={isRegenerating}
            className="bg-[#33b864] hover:bg-[#2a9d54] text-white"
          >
            {isRegenerating ? (
              <>
                <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <Sparkles className="h-4 w-4 mr-1.5" />
                Regenerate
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SectionRegenerateDialog;
